import { z } from 'zod';

import type { StaticFeatureFlags } from './staticFeatureFlags.types';

const staticFeatureFlagsSchema = z.record(z.string(), z.boolean());

let validated: StaticFeatureFlags | null = null;

/**
 * GW-8339: Reads the deploy-time-injected window.__FEATURE_FLAGS__ static map.
 * No network call — the value is set synchronously by the blocking
 * <script src="/feature-flags.js"> tag in index.html before app code runs.
 */
export function getStaticFeatureFlags(): StaticFeatureFlags {
  if (validated) return validated;

  const raw = (globalThis as unknown as Window).__FEATURE_FLAGS__;
  if (!raw) {
    throw new Error(
      'Static feature flags not available. Ensure feature-flags.js is loaded before app scripts.'
    );
  }

  const result = staticFeatureFlagsSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(', ');
    throw new Error(`Static feature flags validation failed: ${issues}`);
  }

  validated = result.data;
  return validated;
}

/**
 * Reads a single flag's static value. Unknown flag names return `false`
 * (matches the old API's 404-treated-as-disabled behavior).
 */
export function getStaticFeatureFlag(flagName: string): boolean {
  return getStaticFeatureFlags()[flagName] ?? false;
}

/** Reset validated cache -- test use only. */
export function _resetStaticFeatureFlagsForTesting(): void {
  validated = null;
}
